import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'two-paths', title: '1. Two Ways to Connect Chat.co' },
  { id: 'direct-api', title: '2. When to Use the Direct API' },
  { id: 'mcp', title: '3. When to Use MCP' },
  { id: 'auth-permissions', title: '4. Authentication & Permissions' },
  { id: 'scoped-vs-unscoped', title: '5. Scoped vs Unscoped Access' },
  { id: 'security-model', title: '6. Security Model' },
  { id: 'decision-guide', title: '7. Quick Decision Guide' },
  { id: 'common-mistakes', title: '8. Common Mistakes' },
];

const ApiVsMcpPage = () => {
  return (
    <>
      <NextSeo
        title="API vs MCP: Choose the Right Chat.co Integration"
        description="Understand the difference between Chat.co's direct API and MCP integration. Learn which option fits your use case, permission model, and workflow."
        canonical="https://chat.co/docs/api-vs-mcp"
        openGraph={{
          url: 'https://chat.co/docs/api-vs-mcp',
          title: 'API vs MCP: Choose the Right Chat.co Integration | Chat.co Docs',
          description:
            "Understand the difference between Chat.co's direct API and MCP integration. Learn which option fits your use case, permission model, and workflow.",
          type: 'article',
          article: {
            publishedTime: '2026-03-06T00:00:00Z',
            modifiedTime: '2026-03-06T00:00:00Z',
            section: 'API Reference',
            tags: ['api', 'mcp', 'integrations', 'permissions', 'agent login'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co API vs MCP Guide',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="API Reference"
        categoryColor="text-blue-700"
        title="API vs MCP: Choose the Right Chat.co Integration"
        description="Use this guide to decide whether you should call Chat.co directly over HTTP or connect an AI client through MCP."
        readingTime="9 min read"
        lastUpdated="March 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'API Documentation',
          href: PageLink.docsApiReference,
        }}
        nextArticle={{
          title: 'MCP Agent Setup',
          href: PageLink.docsMcpAgentSetup,
        }}
        keyTakeaways={[
          'Use the direct API when you are building your own app or backend integration',
          'Use MCP when you want Claude Desktop, Cursor, or another AI client to operate on Chat.co',
          'Dashboard-generated API keys are narrower than MCP device-login keys by default',
          'Scoped keys limit access to one chatbot; unscoped keys enable broader account operations',
          'Most teams benefit from documenting both paths separately and linking them together',
        ]}>
        <p className="lead text-lg text-gray-700">
          Chat.co gives you two different integration paths. They both rely on the same underlying
          platform, but they are built for different workflows. If you are deciding between the
          direct API and MCP, this guide will help you choose the right starting point.
        </p>

        <div className="my-8 rounded-xl border border-blue-200 bg-blue-50 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
              <Icon icon="tabler:route" className="text-xl text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900">Short Version</h4>
              <p className="mt-1 text-blue-800">
                Build with the{' '}
                <Link href={PageLink.docsApiReference} className="font-medium underline">
                  direct API
                </Link>{' '}
                when your software needs deterministic HTTP requests. Use{' '}
                <Link href={PageLink.docsMcpAgentSetup} className="font-medium underline">
                  MCP
                </Link>{' '}
                when you want an AI client to manage chatbots, content, and analytics through tools.
              </p>
            </div>
          </div>
        </div>

        <section id="two-paths" className="scroll-mt-24">
          <h2>1. Two Ways to Connect Chat.co</h2>

          <p>The two integration paths serve different jobs:</p>

          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Option
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Best For
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    How It Works
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Direct API
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Custom apps, backend services, websites, mobile products
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Your software sends HTTP requests to Chat.co endpoints using an API key.
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    MCP
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    AI clients like Claude Desktop or Cursor acting on your Chat.co workspace
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    A local MCP server translates tool calls from your AI client into Chat.co API
                    requests.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            You can use both. Many teams use the direct API for production software and MCP for
            internal operations, experimentation, and content management.
          </p>
        </section>

        <section id="direct-api" className="scroll-mt-24">
          <h2>2. When to Use the Direct API</h2>

          <p>
            The direct API is the right choice when you want explicit control over requests and
            responses.
          </p>

          <h3>Choose the direct API when:</h3>
          <ul>
            <li>You are embedding chat into a website or product</li>
            <li>You need predictable request and response handling in code</li>
            <li>You want to manage authentication entirely inside your own backend</li>
            <li>You are integrating Chat.co into an app, workflow engine, or internal tool</li>
            <li>You only need chat and conversation access for a specific bot</li>
          </ul>

          <div className="my-6 rounded-lg border border-gray-200 bg-white p-5">
            <h4 className="font-semibold text-gray-900">Typical direct API pattern</h4>
            <ol className="mt-3 space-y-1 text-sm text-gray-600">
              <li>1. Generate an API key in Chat.co Settings</li>
              <li>2. Assign it to a chatbot when appropriate</li>
              <li>3. Store it in your backend or secure vault</li>
              <li>4. Call the Client API endpoints directly over HTTPS</li>
            </ol>
          </div>

          <p>
            If this matches your use case, start with the{' '}
            <Link
              href={PageLink.docsApiReference}
              className="font-medium text-primary-600 hover:underline">
              API documentation
            </Link>
            .
          </p>
        </section>

        <section id="mcp" className="scroll-mt-24">
          <h2>3. When to Use MCP</h2>

          <p>
            MCP is the better choice when you want an AI client to use Chat.co as a tool-enabled
            workspace instead of writing custom HTTP logic yourself.
          </p>

          <h3>Choose MCP when:</h3>
          <ul>
            <li>
              You want Claude Desktop, Cursor, or another MCP-compatible client to work with Chat.co
            </li>
            <li>
              You want natural-language actions like “update this bot’s prompt” or “upload this
              document”
            </li>
            <li>You want a local AI client to manage content, analytics, or chatbot settings</li>
            <li>
              You want to approve access in the browser instead of manually copying API keys into
              the client
            </li>
          </ul>

          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">What MCP feels like</h4>
              <p className="mt-2 text-sm text-gray-600">
                Your AI client sees Chat.co as a collection of tools: list bots, update prompts,
                start crawls, upload documents, review leads, and more.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">What MCP is not</h4>
              <p className="mt-2 text-sm text-gray-600">
                It is not a hosted browser experience. It is a local integration path that runs on
                your machine and uses an approved API key behind the scenes.
              </p>
            </div>
          </div>

          <p>
            If this is what you want, go next to the{' '}
            <Link
              href={PageLink.docsMcpAgentSetup}
              className="font-medium text-primary-600 hover:underline">
              MCP agent setup guide
            </Link>
            .
          </p>
        </section>

        <section id="auth-permissions" className="scroll-mt-24">
          <h2>4. Authentication & Permissions</h2>

          <p>This is the most important difference for customers to understand.</p>

          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Topic
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Direct API
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    MCP
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Key creation
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Generated manually from the dashboard
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Created through agent login and approved in your browser
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Default shape
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Typically used for chat-focused API access
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Typically used for broader tool access unless you narrow it
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Where the key lives
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Your app config or secret manager
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    A local key file used by the MCP server
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Ideal permission model
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Narrow and bot-scoped whenever possible
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Use only the scopes the agent actually needs
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="my-6 rounded-xl border border-amber-200 bg-amber-50 p-6">
            <h4 className="font-semibold text-amber-900">Why this matters</h4>
            <p className="mt-2 text-amber-800">
              If you approve an MCP client with broad scopes, that client can do much more than send
              messages. It may be able to update chatbot settings, manage content, start crawl jobs,
              or read analytics depending on the scopes you approved.
            </p>
          </div>
        </section>

        <section id="scoped-vs-unscoped" className="scroll-mt-24">
          <h2>5. Scoped vs Unscoped Access</h2>

          <p>
            Both integration styles can use chatbot-scoped or broader account access, but the
            tradeoff is especially important for MCP.
          </p>

          <h3>Use a scoped key when:</h3>
          <ul>
            <li>You want to limit access to one chatbot</li>
            <li>You are connecting a client for a single team, workflow, or assistant</li>
            <li>You want the smallest practical blast radius</li>
          </ul>

          <h3>Use an unscoped key when:</h3>
          <ul>
            <li>You need to create new chatbots</li>
            <li>You need account-level analytics or message allocation views</li>
            <li>You want one AI client to operate across multiple bots you own</li>
          </ul>

          <p>
            In practice, many teams start broad in a staging environment, learn which tools matter,
            and then tighten scope for production use.
          </p>
        </section>

        <section id="security-model" className="scroll-mt-24">
          <h2>6. Security Model</h2>

          <p>The safest way to think about the two models is this:</p>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Direct API security</h4>
              <p className="mt-2 text-sm text-gray-600">
                Treat the API key like any other backend credential. Store it server-side, rotate it
                periodically, and keep it out of browser code and mobile bundles.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">MCP security</h4>
              <p className="mt-2 text-sm text-gray-600">
                Treat MCP as local delegated access. You are allowing an AI client on your machine
                to call Chat.co tools using a locally stored API key. Approval, scope, expiry, and
                revocation matter more here.
              </p>
            </div>
          </div>

          <p>For an MCP connection, you should document:</p>
          <ul>
            <li>Which chatbot or account scope it has</li>
            <li>Which scopes it was granted</li>
            <li>Where the key file is stored</li>
            <li>Who is allowed to approve or renew the connection</li>
          </ul>
        </section>

        <section id="decision-guide" className="scroll-mt-24">
          <h2>7. Quick Decision Guide</h2>

          <div className="my-6 rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="mt-0">Use the direct API if you are saying:</h3>
            <ul className="mt-3">
              <li>“I need to embed chat in my product.”</li>
              <li>“I want my backend to send and receive messages.”</li>
              <li>“I need predictable JSON responses in code.”</li>
            </ul>
          </div>

          <div className="my-6 rounded-lg border border-gray-200 bg-white p-5">
            <h3 className="mt-0">Use MCP if you are saying:</h3>
            <ul className="mt-3">
              <li>“I want Claude Desktop or Cursor to manage my Chat.co workspace.”</li>
              <li>“I want to ask an AI client to update prompts, upload docs, or review leads.”</li>
              <li>“I do not want to hand-wire every capability as raw API calls.”</li>
            </ul>
          </div>

          <p>
            Still unsure? Start with the direct API for customer-facing production traffic, then add
            MCP for internal operator workflows once your team understands the permission model.
          </p>
        </section>

        <section id="common-mistakes" className="scroll-mt-24">
          <h2>8. Common Mistakes</h2>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">
                Mistake: Treating MCP as “just another API key”
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                MCP is a local tool integration, not just a static credential pasted into code. It
                deserves its own setup, approval, and revocation guidance.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">
                Mistake: Using a broad key when a scoped key would do
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                Limit access to the specific chatbot or capability set you actually need.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">
                Mistake: Sending direct API keys from the browser
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                Keep API keys server-side or in approved local MCP setups. Do not ship them in
                client-side code.
              </p>
            </div>
          </div>

          <p>
            Ready to set up an AI client? Continue to{' '}
            <Link
              href={PageLink.docsMcpAgentSetup}
              className="font-medium text-primary-600 hover:underline">
              Connect Claude Desktop or Cursor with MCP
            </Link>
            .
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export default ApiVsMcpPage;
